/**
 * Date-range presets. The long presets snap to whole-UTC-day
 * boundaries (end = midnight tomorrow UTC); the short ones (for a rolling-window
 * source such as Live) are rolling `now - N min` windows. `max` uses the ship's available range. All write the start/end/preset
 * atoms; the date-range component renders the buttons and reacts to these atoms.
 *
 * Shared as a module (not a component method) because the data-source switch in
 * the shell also applies a default preset (`1h`) when switching to a
 * rolling-window source.
 */

import {
  startDate,
  endDate,
  activePreset,
  shipDateRange,
  type DatePreset,
} from '../../state/store.ts';
import { presetWindow, shiftWindow, toDatetimeLocal, type WindowShift } from '../../utils/dates.ts';
import { scheduleEstimate } from '../../state/estimate.ts';
import { syncToUrl } from '../../state/url-sync.ts';

export function setPresetActive(preset: DatePreset): void {
  activePreset.set(preset);
}

/**
 * Apply a named preset. Every window comes from `presetWindow` - the one
 * definition the dashboard tiles and restored links use too - except `max`,
 * which needs this ship's metadata, and `custom`, which only freezes the dates
 * already there.
 */
export function applyDatePreset(preset: DatePreset): void {
  if (preset === 'custom') {
    setPresetActive('custom');
    syncToUrl();
    return;
  }

  if (preset === 'max') {
    const range = shipDateRange.value;
    if (!range?.min_date || !range?.max_date) return;
    startDate.set(toDatetimeLocal(range.min_date));
    endDate.set(toDatetimeLocal(range.max_date));
  } else {
    const window = presetWindow(preset);
    if (!window) return;
    startDate.set(window.start);
    endDate.set(window.end);
  }
  setPresetActive(preset);
  scheduleEstimate();
  syncToUrl();
}

/**
 * Move the current window back - same period last week / month / year, or the
 * day a week before it starts. The result is fixed dates, so the preset becomes
 * `custom`: "last month's version of Previous month" is not something that
 * should keep rolling.
 */
export function shiftDateWindow(shift: WindowShift): void {
  const moved = shiftWindow({ start: startDate.value, end: endDate.value }, shift);
  if (!moved) return;
  startDate.set(moved.start);
  endDate.set(moved.end);
  setPresetActive('custom');
  scheduleEstimate();
  syncToUrl();
}
